import type { HTMLAttributes, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { Layout } from '../../../types';
import { useBanner } from '../../bannerProvider';

type SideMediaContainer = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    layout: Layout;
    areaWidth: number;
  }>;

export function SideMediaContainer({
  children,
  layout,
  areaWidth,
  ...forwardingProps
}: SideMediaContainer) {
  const {
    stretchBackground,
    outerMargin,
    contentVerticalPadding,
    contentHorizontalPadding,
    borderRadius
  } = useBanner();

  const isBannerLayout = [Layout.LeftBanner, Layout.RightBanner].includes(
    layout
  );

  return (
    <Container
      {...forwardingProps}
      _layout={layout}
      _isBannerLayout={isBannerLayout}
      _stretchBackground={stretchBackground}
      _outerMargin={outerMargin}
      _verticalPadding={contentVerticalPadding}
      _horizontalPadding={contentHorizontalPadding}
      _borderRadius={borderRadius}
      _areaWidth={areaWidth}
    >
      {children}
    </Container>
  );
}

const Container = styled.div<{
  _layout: Layout;
  _isBannerLayout: boolean;
  _stretchBackground: boolean;
  _outerMargin: number;
  _verticalPadding: number | [number, number];
  _horizontalPadding: number;
  _borderRadius: number | undefined;
  _areaWidth: number;
}>`
  ${({
    _layout,
    _isBannerLayout,
    _stretchBackground,
    _outerMargin,
    _verticalPadding,
    _horizontalPadding,
    _borderRadius,
    _areaWidth
  }) => css`
    position: relative;
    flex-basis: ${_isBannerLayout
      ? `calc(${_areaWidth}% + ${_outerMargin}px)`
      : `${_areaWidth}%`};

    &:not(:first-child) {
      margin-left: ${_horizontalPadding}px;
    }

    &:not(:last-child) {
      margin-right: ${_horizontalPadding}px;
    }

    /** Banner Layout Styles*/
    ${_isBannerLayout &&
    css`
      margin: ${typeof _verticalPadding === 'number'
        ? `-${_verticalPadding}px 0;`
        : `-${_verticalPadding[0]}px 0 -${_verticalPadding[1]}px;`};

      ${_borderRadius &&
      css`
        overflow: hidden;
        border-radius: ${_layout === Layout.LeftBanner
          ? `${_borderRadius}px 0 0 ${_borderRadius}px`
          : `0 ${_borderRadius}px ${_borderRadius}px 0`};
      `};

      &:first-child {
        margin-left: -${_stretchBackground
            ? _outerMargin
            : _horizontalPadding}px;
      }

      &:last-child {
        margin-right: -${_stretchBackground
            ? _outerMargin
            : _horizontalPadding}px;
      }

      & + & {
        margin-left: 0;
      }
    `}
  `}
`;
